import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useResponsive } from '@sg/garnish';

import { useStripePayment } from '@order/integrations/stripe';
import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import { type useCreditCardOptions } from '../../../../hooks';
import {
  type GiftCardCheckoutFormFieldRules,
  type GiftCardCheckoutFormReturn,
} from '../../form';
import { GiftCardCheckoutFormContainer } from './components';
import { useCustomerPaymentInfoQuery } from './GraphQL/CustomerPaymentInfo.generated';
import {
  GiftCardCheckoutAmount,
  GiftCardCheckoutDeliveryDate,
  GiftCardCheckoutDeliveryMethod,
  GiftCardCheckoutDetails,
  GiftCardCheckoutLedger,
  GiftCardCheckoutPaymentElement,
  GiftCardCheckoutPaymentProvider,
  GiftCardCheckoutSubmit,
} from './sections';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutFormV2 = (props: GiftCardCheckoutFormProps) => {
  const { form } = props;

  const paymentMethods = useMemo(() => {
    return mapPaymentMethods(props.paymentMethods);
  }, [props.paymentMethods]);

  return (
    <GiftCardCheckoutPaymentProvider form={form}>
      <GiftCardCheckoutFormReady {...props} paymentMethods={paymentMethods} />
    </GiftCardCheckoutPaymentProvider>
  );
};

const GiftCardCheckoutFormReady = (props: GiftCardCheckoutFormReadyProps) => {
  const { sectionTitles, form, rules, submitForm, paymentMethods } = props;

  const { match } = useResponsive();

  const [{ data }] = useCustomerPaymentInfoQuery({
    requestPolicy: 'cache-only',
  });

  const stripeCustomerId = data?.customerPaymentInfo?.id;
  const stripeCustomerKey = data?.customerPaymentInfo?.paymentEphemeralKey;
  const { amount } = form.watch();
  const { isSubmitting, errors, isValid } = form.formState;
  const canSubmit = isValid && Object.keys(errors).length === 0;

  const onConfirm = useCallback(
    (paymentMethodId: string) => {
      form.setValue('paymentMethodId', paymentMethodId);
      return submitForm();
    },
    [form, submitForm],
  );

  const { track } = useTelemetry();
  const trackStripeUsage = useCallback(
    (payload: unknown) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );

  const { embeddedPaymentElementView, confirm: confirmPayment } =
    useStripePayment({
      total: amount,
      stripeCustomerId,
      stripeCustomerKey,
      stripePublicKey,
      paymentMethods,
      trackStripeUsage,
      onConfirm,
    });

  return (
    <View style={match([null, styles.containerSM])}>
      <GiftCardCheckoutFormContainer>
        <GiftCardCheckoutAmount
          sectionTitle={sectionTitles?.amount}
          form={form}
          rules={rules}
        />

        <GiftCardCheckoutDetails
          sectionTitle={sectionTitles?.details}
          form={form}
          rules={rules}
        />

        <GiftCardCheckoutDeliveryDate
          sectionTitle={sectionTitles?.deliveryDate}
          form={form}
          rules={rules}
        />

        <GiftCardCheckoutDeliveryMethod
          sectionTitle={sectionTitles?.deliveryMethod}
          sectionTitleEmailOnly={sectionTitles?.recipientEmail}
          form={form}
        />

        <GiftCardCheckoutPaymentElement
          embeddedPaymentElementView={embeddedPaymentElementView}
        />
      </GiftCardCheckoutFormContainer>

      <GiftCardCheckoutLedger form={form} />

      <GiftCardCheckoutSubmit
        isDisabled={!canSubmit}
        isSubmitting={isSubmitting}
        onPress={confirmPayment}
      />
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const stripePublicKey = getEnvVars().STRIPE_PUBLIC_KEY;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const mapPaymentMethods = (
  paymentMethods: ReturnType<typeof useCreditCardOptions>['creditCardOptions'],
) => {
  return paymentMethods.map((paymentMethod) => ({
    id: paymentMethod.value,
    description: paymentMethod.label,
    nickname: paymentMethod.label,
    cardType: paymentMethod.cardType as Parameters<
      typeof useStripePayment
    >[0]['paymentMethods'][number]['cardType'],
  }));
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSM: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutFormProps = {
  form: GiftCardCheckoutFormReturn;
  rules: GiftCardCheckoutFormFieldRules;
  paymentMethods: ReturnType<typeof useCreditCardOptions>['creditCardOptions'];
  submitForm: () => Promise<void>;
  sectionTitles?: {
    amount: string;
    details: string;
    deliveryDate: string;
    deliveryMethod: string;
    recipientEmail: string;
    paymentMethod: string;
  };
};

type GiftCardCheckoutFormReadyProps = Omit<
  GiftCardCheckoutFormProps,
  'paymentMethods'
> & {
  paymentMethods: ReturnType<typeof mapPaymentMethods>;
};
